import { countriesQuery } from "@/api/location";
import { listingsQuery, supplierProfileQuery, supplierQuery } from "@/api/supplier";
import { queryClient } from "@/api/utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { ListingStatusBadge } from "@/components/listings/ListingStatusBadge";
import { Markdown } from "@/components/markdown/Markdown";
import { Card } from "@/components/ui/Card";
import { CountryFlagImage } from "@/components/ui/CountryFlagImage";
import { Skeleton } from "@/components/ui/Skeleton";
import { seo } from "@/utils/seo";
import { Box, Field, Flex, Img, Label, QueryHandler, Text } from "@givenwell/components";
import { GetSupplierProfileResponse } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/suppliers/$supplierId/")({
  component: SupplierDetailsTab,
  head: async ({ params }) => {
    const supplier = await queryClient.ensureQueryData(supplierQuery(params.supplierId));
    return {
      meta: seo({ title: supplier.name }),
    };
  },
});

function SupplierDetailsTab() {
  const { supplierId } = Route.useParams();

  const supplier = useQuery(supplierQuery(supplierId));
  const profileQuery = useQuery(supplierProfileQuery(supplierId));
  const countries = useQuery(countriesQuery(false));

  const isLoading = supplier.isLoading || countries.isLoading;

  const country = countries.data?.countries.find(country => country.code === supplier.data?.country_code);

  return (
    <Flex css={{ items: "start", gap: 24 }}>
      <Box css={{ flex: "1 0 0px" }}>
        <SupplierListingsTable />
      </Box>

      <Card css={{ w: 340, pos: "relative" }}>
        <QueryHandler
          query={profileQuery}
          success={({ data: profile }) => (
            <Box
              css={{
                pos: "relative",
                top: -16,
                left: -16,
                width: "calc(100% + 32px)",
              }}
            >
              <ProfileImages profile={profile} />
            </Box>
          )}
        />
        <Flex css={{ flexDirection: "column", gap: 24 }}>
          <Field>
            <Label>Name</Label>
            <Skeleton loaded={!isLoading}>
              <Text>{supplier.data?.name}</Text>
            </Skeleton>
          </Field>

          <Field>
            <Label>Bio</Label>
            <Skeleton loaded={!isLoading}>
              <Markdown markdown={profileQuery.data?.bio || "__None__"} />
            </Skeleton>
          </Field>

          <Field>
            <Label>Country</Label>
            <Skeleton loaded={!isLoading}>
              <Flex css={{ gap: 6 }}>
                <CountryFlagImage countryCode={country?.code || ""} css={{ size: 24 }} />
                {country?.name}
              </Flex>
            </Skeleton>
          </Field>
        </Flex>
      </Card>
    </Flex>
  );
}

function ProfileImages({ profile }: { profile: GetSupplierProfileResponse | undefined }) {
  return (
    <Box
      css={{
        aspectRatio: 32 / 9,
        pos: "relative",
        w: "100%",
        mb: 40,
        backgroundColor: colors.warmGrey,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.7)",
        roundedT: 12,
      }}
    >
      {profile?.cover_image_media && (
        <Img
          css={{
            pos: "absolute",
            left: "0",
            top: "0",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: "black",
          }}
          src={profile.cover_image_media.url}
        />
      )}
      <Box
        css={{
          pos: "absolute",
          top: "100%",
          left: 16,
          transform: "translateY(-50%)",
          border: "4px solid white",
          rounded: "50%",
          overflow: "hidden",
        }}
      >
        <Img
          css={{
            size: 80,
            backgroundColor: "black",
            border: "none",
            objectFit: "cover",
          }}
          src={profile?.profile_image_media?.url}
        />
      </Box>
    </Box>
  );
}

function SupplierListingsTable() {
  const { supplierId } = Route.useParams();
  const listings = useQuery(listingsQuery(supplierId, undefined));

  const rows = listings.data ?? [];

  return (
    <DataTable>
      <DataTableBody
        rounded="full"
        data={rows}
        status={listings.status}
        emptyStateMessage="No listings found"
        columns={[
          {
            cell: listing => <span>{listing.title}</span>,
            header: "Title",
          },
          {
            header: "Description",
            cell: listing => (
              <div style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", color: colors.text2 }}>
                <Markdown markdown={listing.description} inline />
              </div>
            ),
          },
          {
            cell: listing => <ListingStatusBadge status={listing.version_status} version={listing.version} />,
            header: "Status",
          },
        ]}
        dataKey={"id"}
      />
    </DataTable>
  );
}
